// it's important that this import its first
import "@/debug/vConsole";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import ReactDOM from "react-dom/client";

import "@fontsource-variable/noto-sans-arabic";
import "./styles.css";
import { initializeGrowthBook } from "@/lib/growthbook";
import { isProduction } from "@/utils/constants";

import { platformApi } from "./platform";
import { queryClient } from "./queryClient";
import { RouterWrapper } from "./RouterWrapper";
import { routeTree } from "./routeTree.gen";

platformApi.init?.();
await initializeGrowthBook();

// Set up a Router instance
const router = createRouter({
  routeTree,
  context: { queryClient },
  scrollRestoration: true,
  scrollToTopSelectors: [() => document.body],
  defaultPreload: false,
  defaultOnCatch: (error) => {
    if (!isProduction) {
      console.error(error);
    }
    Sentry.captureException(error);
  },
  InnerWrap: RouterWrapper,
  defaultViewTransition: {
    types: ({ fromLocation, toLocation }) => {
      if (!fromLocation) return false;

      const fromPath = fromLocation.pathname;
      const toPath = toLocation.pathname;

      const fromIndex = fromLocation.state.__TSR_index;
      const toIndex = toLocation.state.__TSR_index;
      const isBack = fromIndex > toIndex;

      const shouldAnimate =
        [fromPath, toPath].includes("/flights/return/list") || // going from departure flight selection to return flight selection or from return flight selection to offer details
        (toPath === "/flights/departure/list" && isBack) || // going back to departure flight selection from return flight selection or offer details
        (fromPath === "/flights/departure/list" && !isBack); // going to offer details (one-way flight)

      if (!shouldAnimate) return false;

      return isBack ? ["nav-back"] : ["nav-forward"];
    },
  },
});

if (
  ["production", "uat", "staging"].includes(
    import.meta.env.VITE_PUBLIC_ENVIRONMENT,
  )
) {
  Sentry.init({
    dsn: "https://6527f31665e26b7c439b4ab54545c562@o4505543468974080.ingest.us.sentry.io/4507934321541120",
    environment: import.meta.env.VITE_PUBLIC_ENVIRONMENT,
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.extraErrorDataIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        unmask: ["[data-sentry-unmask]"],
        unblock: ["[data-sentry-unblock]"],
        networkDetailAllowUrls: [
          /\/api\/v\d+\/mappings/,
          /\/api\/v\d+\/packages/,
          /\/api\/v\d+\/flights$/, // /api/v{version}/flights
          /\/api\/v\d+\/flights\/search$/, // /api/v{version}/flights/search
          /\/api\/v\d+\/flights\/[^/]+\/availability$/, // /api/v{version}/flights/{offerId}/availability
          /\/api\/v\d+\/hotels$/, // /api/v{version}/hotels
          /\/api\/v\d+\/hotels\/search$/, // /api/v{version}/hotels/search
          /\/api\/v\d+\/hotels\/search\/progress$/, // /api/v{version}/hotels/search/progress
          /\/api\/v\d+\/hotels\/[^/]+\/offers$/, // /api/v{version}/hotels/{hotelId}/offers
          /\/api\/v\d+\/hotels\/[^/]+\/availability$/, // /api/v{version}/hotels/{offerId}/availability
        ],
      }),
    ],
    replaysSessionSampleRate: isProduction ? 0.25 : 1.0,
    replaysOnErrorSampleRate: 1.0,
    tracesSampleRate: 1.0,
    enableLogs: true,
  });
}

// Register things for typesafety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root")!;

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>,
  );
}
